import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { type CollectionSummary, deleteScript, getCollection, getHistory, listCollections } from "../api";
import { unlockAudio } from "../audio";
import { Badge } from "../components/Badge";
import { btnInk } from "../components/Cram";
import { LockIcon, SubscribeOffer } from "../components/Paywall";
import { ScriptSheet } from "../components/ScriptSheet";
import { resumeFromHistory } from "../lib/rsvp";
import { Empty, ErrorBox, Loading, useResource } from "../ui";

export function Library() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [scripting, setScripting] = useState(false);
  const [version, setVersion] = useState(0);
  const collections = useResource(listCollections, [params.toString(), version]);
  const history = useResource(() => getHistory(1).catch(() => []), []);

  const start = (to: string) => {
    unlockAudio();
    navigate(to);
  };

  const list = collections.data ?? [];
  const last = history.data?.[0];
  const firstOpen = list.find((c) => !c.locked && c.passageCount > 0);
  const hero = last
    ? {
        to: `/app/p/${resumeFromHistory(last)}`,
        label: "続きから",
        sub: [list.find((c) => c.slug === last.collectionSlug)?.title, last.passageTitle].filter(Boolean).join(" ・ "),
      }
    : firstOpen && { to: `/app/c/${firstOpen.slug}`, label: "はじめる", sub: firstOpen.title };

  if (!collections.data && !collections.error) return <Loading />;
  if (collections.error) return <ErrorBox message={collections.error} />;

  return (
    <div className="mx-auto max-w-2xl">
      {hero && (
        <button
          type="button"
          onClick={() => start(hero.to)}
          className="press flex w-full items-center gap-4 border-2 border-ink bg-shu px-5 py-4 text-left text-white hover:bg-shu-deep"
        >
          <span className="text-2xl leading-none">▶</span>
          <span className="min-w-0">
            <span className="block text-[18px] font-black">{hero.label}</span>
            {hero.sub && <span className="block truncate text-[12px] font-bold opacity-90">{hero.sub}</span>}
          </span>
        </button>
      )}

      <button type="button" onClick={() => setScripting(true)} className={`${btnInk} mt-3 w-full`}>
        ＋ 自分の原稿で練習する
      </button>
      <ScriptSheet open={scripting} onClose={() => setScripting(false)} onCreated={(id) => navigate(`/app/p/${id}`)} />

      {!list.length ? (
        <div className="mt-6">
          <Empty>公開中のコレクションはまだありません。</Empty>
        </div>
      ) : (
        <ul className="mt-6 border-2 border-ink bg-sheet">
          {list.map((c) => (
            <Row key={`${c.slug}-${version}`} c={c} onStart={start} onChanged={() => setVersion((v) => v + 1)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({ c, onStart, onChanged }: { c: CollectionSummary; onStart: (to: string) => void; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const sample = c.locked && c.samplePassageId;

  return (
    <li className="border-b border-ink last:border-b-0">
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={() =>
            c.locked ? (sample ? onStart(`/app/p/${c.samplePassageId}`) : setOpen((v) => !v)) : onStart(`/app/c/${c.slug}`)
          }
          className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left hover:bg-marker/40"
        >
          <span className="w-4 shrink-0 text-center text-shu">{c.locked && !sample ? <LockIcon /> : "▶"}</span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="text-[15px] leading-snug font-black">{c.title}</span>
              {c.status !== "published" && <Badge tone="admin">{c.status}</Badge>}
              {c.access === "free" && !c.owned && <Badge tone="free">無料</Badge>}
              {sample && <Badge tone="free">1 本無料</Badge>}
            </span>
            {c.description && <span className="mt-0.5 block truncate text-[12px] text-ink-2">{c.description}</span>}
          </span>
          <span className="shrink-0 font-mono text-[11px] text-ink-3">
            {c.level && `${c.level} · `}
            {c.playedCount}/{c.passageCount}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label="パッセージを選ぶ"
          title="パッセージを選ぶ"
          className="w-11 shrink-0 border-l border-rule text-[12px] text-ink-3 hover:bg-marker/40 hover:text-ink"
        >
          {open ? "▴" : "▾"}
        </button>
      </div>
      {open && <Passages c={c} onStart={onStart} onChanged={onChanged} />}
    </li>
  );
}

function Passages({ c, onStart, onChanged }: { c: CollectionSummary; onStart: (to: string) => void; onChanged: () => void }) {
  const { data, loading, error } = useResource(() => getCollection(c.slug), [c.slug]);
  const [confirming, setConfirming] = useState<string | null>(null);
  const remove = (id: string) => (confirming === id ? deleteScript(id).then(onChanged, () => {}) : setConfirming(id));
  if (loading) return <p className="border-t border-rule px-4 py-2 text-[12px] text-ink-3">…</p>;
  if (error || !data) return <p className="border-t border-rule px-4 py-2 text-[12px] font-bold text-shu">{error}</p>;
  return (
    <div className="border-t border-rule bg-paper">
      {!data.passages.length && <p className="px-4 py-2 text-[12px] text-ink-2">パッセージはまだありません。</p>}
      <ol>
        {data.passages.map((p, i) => (
          <li key={p.id} className="flex items-stretch">
            <button
              type="button"
              disabled={p.locked}
              onClick={() => onStart(`/app/p/${p.id}`)}
              className="flex min-w-0 flex-1 items-center gap-3 py-2 pr-4 pl-11 text-left text-[13px] hover:bg-marker/40 disabled:text-ink-3 disabled:hover:bg-transparent"
            >
              <span className="w-6 shrink-0 font-mono text-[11px] text-ink-3">{String(i + 1).padStart(2, "0")}</span>
              <span className="min-w-0 flex-1 truncate font-bold">{p.title ?? p.slug}</span>
              {p.locked && (
                <span className="text-ink-3">
                  <LockIcon />
                </span>
              )}
              {p.audioStatus !== "ready" && <Badge tone="admin">{p.audioStatus}</Badge>}
              {p.playCount > 0 && <span className="text-[11px] font-bold text-shu">✓</span>}
            </button>
            {c.owned && (
              <button
                type="button"
                onClick={() => remove(p.id)}
                aria-label="この原稿を削除"
                title="この原稿を削除"
                className={`shrink-0 px-3 text-[12px] font-bold ${confirming === p.id ? "bg-shu text-white" : "text-ink-3 hover:text-shu"}`}
              >
                {confirming === p.id ? "削除する" : "×"}
              </button>
            )}
          </li>
        ))}
      </ol>
      {c.locked && (
        <div className="border-t border-rule px-4 py-4">
          <p className="mb-3 text-[13px] font-bold">残りのパッセージは使い放題プランで再生できます。</p>
          <SubscribeOffer />
        </div>
      )}
    </div>
  );
}
